import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { isNull } from "lodash";
import QRCode from "react-qr-code";
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import lzSAuthAppConfigsAtom from "@lz-store/atoms/Auth/appConfigs";
import useLzFApiCall from "@lz-services/Api";
import { useLzOnMount } from "@lz-services/Hooks";
import LzXSpinner from "@lz/components/commons/Spinner";
import SalesInvoiceModel from "./sales-invoice.model";
import ConfigurationsModel from "../../System/configurations.model";
import ImgGwpPrintLogo from "../../../assets/images/gwp-print-logo.jpg";
import ImgFbrDILogo from "../../../assets/images/fbr-di-logo.jpg";
import SalesInvoiceDetailModel from "./sales-invoice-detail.model";
import { lzFGetFormattedDate } from "@lz/services/Moment";
import { lzFGetAmountInWords } from "@lz/services/Utils";
import { getFormattedAmount } from "services/Utils";

const SalesInvoicePrint = () => {
    const routeParams = useParams();
    const authAppConfigsState = useAtomValue(lzSAuthAppConfigsAtom);
    const [record, setRecord] = useState(null);
    const apiCall = useLzFApiCall();

    const load = async () => {
        setRecord(null);
        const data = await apiCall('sales-invoices/' + (+(routeParams.id || 0)));
        if (data) {
            setRecord(data);
        }
    };

    // Call only for the first time when component is initially loaded
    useLzOnMount(() => {
        load();
    });

    if (isNull(record)) {
        return <LzXSpinner />;
    }

    const appConfigs = new ConfigurationsModel(authAppConfigsState);
    const si = new SalesInvoiceModel(record);

    return (
        <Paper elevation={0} className="lz-hide-bg-on-print">
            <Container>

                <div className="lz-hide-on-print">
                    <div style={{ textAlign: 'right', paddingTop: 12, paddingBottom: 12 }}>
                        <Button type="button" color="primary" variant="contained" onClick={() => window.print()} style={{ marginRight: 10 }}>Print</Button>
                        <Button type="button" color="secondary" onClick={() => load()}>Reload</Button>
                        <Button type="button" color="secondary" onClick={() => window.close()}>Close</Button>
                    </div>
                </div>

                <div className="lz-report-wrapper">

                    <div style={{ height: '98vh', display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif' }}>

                        <div style={{ fontSize: 11, lineHeight: 1.1, paddingTop: 18 }}>
                            <div style={{ display: "flex" }}>
                                <img src={ImgGwpPrintLogo} alt="GWP Print Logo" style={{ marginTop: 4, width: 72, height: 72 }} />
                                <div style={{ paddingLeft: 12 }}>
                                    <h4 style={{ margin: 0, marginBottom: 6, fontSize: 18, fontFamily: 'cursive' }}>{appConfigs.company.name}</h4>
                                    <div>{appConfigs.company.address}</div>
                                    <div>Ph. {appConfigs.company.phone_nos}</div>
                                    <div>fax. {appConfigs.company.fax_nos}</div>
                                    <div>Sales Tax Reg. No. {appConfigs.company.strn}, NTN No. {appConfigs.company.ntn}</div>
                                </div>
                            </div>
                            <div style={{ marginTop: 8 }}>{appConfigs.company.slogan}</div>
                            <div style={{ paddingBottom: 8, borderBottom: '2px solid #7a6a2a', marginBottom: 12 }}>{appConfigs.company.url}</div>
                        </div>

                        <h1 style={{ margin: 0, fontSize: 21, textAlign: 'center', textTransform: 'uppercase', color: '#6a6a6a' }}>Sales Tax {si.is_fbr_registered ? '' : 'Proforma'} Invoice</h1>

                        <table width="100%" cellPadding={2} style={{ marginTop: 12, fontSize: 13 }}>
                            <tr>
                                <td width="140">Buyer's Name :</td>
                                <td style={{ fontWeight: 'bold' }}>{si.buyer_name}</td>
                                <td width="60" style={{ whiteSpace: 'nowrap' }}>Inv. No. :</td>
                                <td width="120" style={{ textAlign: 'right', fontSize: 16, fontWeight: 600 }}>{si.sales_tax_invoice_no}</td>
                            </tr>
                            <tr>
                                <td>Address :</td>
                                <td>{si.buyer_address}</td>
                                <td>Date :</td>
                                <td style={{ textAlign: 'right' }}>{lzFGetFormattedDate(si.invoice_date)}</td>
                            </tr>
                            <tr>
                                <td>Sales Tax Reg. No. :</td>
                                <td colSpan={si.is_fbr_registered ? 1 : 3}>{si.buyer_strn}</td>
                                {si.is_fbr_registered &&
                                    <React.Fragment>
                                        <td style={{ whiteSpace: 'nowrap' }}>FBR Digital Invoice No. :</td>
                                        <td style={{ textAlign: 'right', fontSize: 16, fontWeight: 600 }}>{si.fbr_invoice_no}</td>
                                    </React.Fragment>
                                }
                            </tr>
                            <tr>
                                <td>NTN No. :</td>
                                <td colSpan={3}>{si.buyer_ntn}</td>
                            </tr>
                            <tr>
                                <td>CNIC No. :</td>
                                <td colSpan={3}>{si.buyer_cnic}</td>
                            </tr>
                        </table>

                        <table width="100%" cellPadding={4} cellSpacing={0} border={1} style={{
                            flexGrow: 1,
                            marginTop: 18,
                            fontSize: 11,
                            borderColor: 'gray',
                            borderCollapse: 'collapse',
                        }}>
                            <tr style={{ backgroundColor: 'lightgray', verticalAlign: 'top' }}>
                                <th style={{ width: 5, whiteSpace: 'nowrap' }}>S#</th>
                                <th style={{ textAlign: 'left' }}>Description of goods</th>
                                <th style={{ width: 5, whiteSpace: 'nowrap' }}>Net.Wt.<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Kgs.</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap' }}>Qty.<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Pcs.</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>Price<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>Gr.Amt.<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>TI.Disc.<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>Exc.S.Tax<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>S.Tax<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                                <th style={{ width: 5, whiteSpace: 'nowrap', textAlign: 'right' }}>Inc.S.Tax<small style={{ display: 'block', marginTop: 2, fontWeight: 'normal' }}>Rs</small></th>
                            </tr>
                            {si.sales_invoice_details.map((rec, dex) => {
                                const sid = new SalesInvoiceDetailModel(
                                    rec,
                                    si.discount_rate,
                                    si.sales_tax_rate,
                                    si.further_tax_rate,
                                    si.is_buyer_registered
                                );
                                return (
                                    <tr key={'sid-' + dex} style={{ verticalAlign: 'bottom' }}>
                                        <td style={{ textAlign: 'center' }}>{sid.sno}</td>
                                        <td>
                                            {0 === dex &&
                                                <div style={{
                                                    paddingBottom: 12,
                                                    fontSize: 14,
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    textDecoration: 'underline',
                                                    textAlign: 'center',
                                                }}>Description Code: {sid.item_hs_code}</div>
                                            }

                                            {(0 === dex || sid.item_type_name !== si.sales_invoice_details[dex - 1].item_type_name) &&
                                                <div style={{ paddingBottom: 12, fontWeight: 'bold', textTransform: 'uppercase', textDecoration: 'underline' }}>{sid.item_type_name}</div>
                                            }

                                            <div style={{ width: 140, float: 'right' }}>{sid.item_quality_name}</div>

                                            {sid.item_size_name}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{getFormattedAmount(sid.weight, true, false, true)}</td>
                                        <td style={{ textAlign: 'center' }}>{getFormattedAmount(sid.quantity, true, false, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(sid.price, true, false, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(sid.amount, true, false, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(sid.discount, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(sid.gross, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(+sid.total_tax, true)}</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(+sid.total, true)}</td>
                                    </tr>
                                );
                            })}
                            <tr style={{ textAlign: 'right' }}>
                                <td colSpan={2}>TOTALS : </td>
                                <td style={{ textAlign: 'center' }}>{getFormattedAmount(si.total_weight, true, false, true)}</td>
                                <td style={{ textAlign: 'center' }}>{getFormattedAmount(si.total_quantity, true, false, true)}</td>
                                <td style={{ textAlign: 'center' }}>-</td>
                                <td>{getFormattedAmount(si.total_amount, true, false, true)}</td>
                                <td>{getFormattedAmount(si.total_discount, true)}</td>
                                <td>{getFormattedAmount(si.total_gross, true)}</td>
                                <td>{getFormattedAmount(si.total_tax, true)}</td>
                                <td>{getFormattedAmount(+si.total_gross + +si.total_tax, true)}</td>
                            </tr>
                            <tr style={{ height: '100%' }}>
                                <td colSpan={9}>&nbsp;</td>
                            </tr>
                        </table>

                        <div style={{ marginTop: 12, display: 'flex' }}>
                            <div style={{ flexGrow: 1, padding: 16 }}>
                                <div>{si.invoice_no}</div>
                                {si.is_fbr_registered &&
                                    <div style={{ margin: 'auto', paddingTop: 30, width: 'fit-content', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <div style={{ margin: 'auto', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                            <img src={ImgFbrDILogo} alt="FBR Digital Invoicing Logo" style={{ marginTop: 4, width: 72, height: 'auto' }} />
                                            <div style={{ width: 72, height: 72 }}>
                                                <QRCode value={si.fbr_invoice_no} style={{ width: '100%', height: '100%' }} />
                                            </div>
                                        </div>
                                        <strong style={{ fontSize: 13 }}>{si.fbr_invoice_no}</strong>
                                    </div>
                                }
                            </div>
                            <div style={{ width: 400 }}>
                                <table width="100%" cellPadding={4} cellSpacing={0} border={1} style={{
                                    borderTop: 0,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    borderColor: 'gray',
                                    borderCollapse: 'collapse',
                                }}>
                                    <tr>
                                        <td>Gross Sales Value :</td>
                                        <td style={{ width: 70, textAlign: 'right' }}>{getFormattedAmount(si.gross_sales_value, true)}</td>
                                    </tr>
                                    <tr>
                                        <td>Less: Trade Incentive Discount @ {si.discount_rate}% :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(si.discount_value, true)}</td>
                                    </tr>
                                    <tr>
                                        <td>Value Excluding Sales Tax & Adv. I. Tax :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(si.excl_st_wht_value, true)}</td>
                                    </tr>
                                    <tr>
                                        <td>Add: Sales Tax @ {si.sales_tax_rate + (si.is_buyer_registered ? 0 : si.further_tax_rate)}% :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(si.sales_tax_value + (si.is_buyer_registered ? 0 : si.further_tax_value), true)}</td>
                                    </tr>
                                    <tr>
                                        <td>Value Including Sales Tax :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(+si.total_gross + +si.total_tax, true)}</td>
                                    </tr>
                                    <tr>
                                        <td>Add: Advance I. Tax @ {si.wht_rate}% :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(si.wht_value, true)}</td>
                                    </tr>
                                    <tr style={{ backgroundColor: 'lightgray', fontWeight: 'bold' }}>
                                        <td>Value Including Sales Tax & Adv. I. Tax :</td>
                                        <td style={{ textAlign: 'right' }}>{getFormattedAmount(si.invoice_value, true)}</td>
                                    </tr>
                                </table>
                            </div>
                        </div>

                        <div style={{ fontSize: 12, lineHeight: 1.2, textTransform: 'uppercase', textAlign: 'right' }}>{lzFGetAmountInWords(si.invoice_value, true)}</div>

                        <div style={{ marginTop: 40, marginBottom: 18, fontSize: 12, fontWeight: 'lighter', color: '#9a9a9a', textAlign: 'right' }}>(this is a digitally generated document and does not require a signature)</div>

                        <div style={{
                            marginTop: 12,
                            borderTop: '2px solid #7a6a2a',
                            paddingTop: 9,
                            textAlign: 'center',
                            fontSize: 11,
                        }}>
                            Head Office: {appConfigs.company.head_office.address}
                            Ph. {appConfigs.company.head_office.phone_nos}
                            Fax: {appConfigs.company.head_office.fax_nos}
                        </div>

                    </div>

                </div>

            </Container>
        </Paper >
    );

};

export default SalesInvoicePrint;
